/* eslint-disable tailwindcss/no-custom-classname */
import { useEffect, useMemo, useRef, useState } from "react";
import Select, { MultiValue } from "react-select";
import {
  createNewWorkout,
  getWorkoutCategories,
} from "../../services/workouts";
import { toast } from "react-toastify";
import {
  Button,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Select as FlowbiteSelect,
  Textarea,
  TextInput,
} from "flowbite-react";
import * as Sentry from "@sentry/react";
import { HiPlus } from "react-icons/hi";

import { difficulty } from "./data.json";
import { IntensityLevel } from "./EditWorkoutModal";
import FileUploadZone from "../media/FileUploadZone";

const AddWorkoutModal = function (props: any) {
  const rootRef = useRef<HTMLDivElement>(null);

  const [isOpen, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  type CategoryOption = { value: string; label: string };

  const [workout, setWorkout]: any = useState({
    title: "",
    description: "",
    categories: [],
    difficulty: null,
    trainer: null,
    rounds: null,
    published: false,
    featured: false,
  });

  const [categories, setCategories] = useState([]);
  const [image, setImage]: any = useState(null);
  const [imageName, setImageName]: any = useState(null);
  const [imageType, setImageType]: any = useState(null);

  const formValid = useMemo(() => {
    return (
      workout.title.trim() !== "" &&
      workout.description.trim() !== "" &&
      Array.isArray(workout.categories) &&
      workout.categories.length > 0 &&
      workout.difficulty != null &&
      workout.trainer != null &&
      workout.rounds != null &&
      image != null
    );
  }, [workout, image]);

  const intensityArray = [
    { id: "Auto Assign", title: "Auto Assign" },
    ...Object.entries(IntensityLevel).map(([key, value]) => ({
      id: value,
      title: key,
    })),
  ];

  const handleChange = (e: any) => {
    const { name, value, checked, type } = e.target;
    setWorkout((prev: any) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const categoryOptions = useMemo(
    () =>
      categories.map((category: any) => ({
        value: category.id,
        label: category.name,
      })),
    [categories],
  );

  const selectedCategoryOptions = useMemo(() => {
    if (!Array.isArray(workout.categories)) {
      return [];
    }

    const categoryIdSet = new Set(workout.categories);

    return categoryOptions.filter((option) => categoryIdSet.has(option.value));
  }, [categoryOptions, workout.categories]);

  const handleCategoriesChange = (
    selectedOptions: MultiValue<CategoryOption>,
  ) => {
    setWorkout((prev: any) => ({
      ...prev,
      categories: selectedOptions.map((option) => option.value),
    }));
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      const new_workout = await createNewWorkout({
        ...workout,
        cover_image: image,
        image_name: imageName,
        image_type: imageType,
      });

      if (new_workout.data.error) {
        toast.error(
          `Unable to create new workout: ${new_workout.data.message}`,
        );
        Sentry.captureMessage("Unable to create new workout", {
          extra: {
            payload: new_workout,
          },
        });
      } else {
        props.getWorkouts();
        setOpen(false);
        setWorkout((prev: any) => ({
          ...prev,
          categories: [],
        }));
        toast.success("New workout created successfully!");
      }
    } catch (e: any) {
      const reqMessage = e?.response?.data?.message;
      const message = Array.isArray(reqMessage)
        ? reqMessage.join(", ")
        : reqMessage;
      toast.error(`Unable to create new workout: ${message || e.message}`);
      Sentry.captureMessage("Unable to create new workout", {
        extra: {
          error: e,
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await getWorkoutCategories();
      setCategories(response.data);
    } catch {
      toast.error("Failed to load workout categories");
      Sentry.captureMessage("Failed to load workout categories");
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  return (
    <>
      <div ref={rootRef}>
        <Button color="primary" onClick={() => setOpen(true)}>
          <div className="flex items-center gap-x-3">
            <HiPlus className="text-xl" />
            Add Workout
          </div>
        </Button>
        <Modal
          onClose={() => setOpen(false)}
          show={isOpen}
          size="3xl"
          position="top-center"
          root={rootRef.current ?? undefined}
        >
          <ModalHeader className="border-b border-gray-200 p-6! dark:border-gray-700">
            <strong>Add New Workout</strong>
          </ModalHeader>
          <ModalBody>
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
              <div>
                <Label htmlFor="firstName">Title</Label>
                <div className="mt-1">
                  <TextInput
                    name="title"
                    value={workout.title}
                    placeholder="Title"
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="lastName">Description</Label>
                <div className="mt-1">
                  <Textarea
                    id="description"
                    name="description"
                    value={workout.description}
                    placeholder="Description"
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div>
                <Label>Category</Label>
                <div className="mt-1">
                  <Select
                    isMulti
                    options={categoryOptions}
                    value={selectedCategoryOptions}
                    onChange={handleCategoriesChange}
                    placeholder="Search and select categories..."
                    className="react-select-container"
                    classNamePrefix="react-select"
                    noOptionsMessage={() => "No categories found"}
                  />
                </div>
              </div>

              <div>
                <Label>Difficulty</Label>
                <div className="mt-1">
                  <FlowbiteSelect
                    value={workout.difficulty ?? ""}
                    onChange={handleChange}
                    name="difficulty"
                  >
                    <option value="" disabled>
                      Choose
                    </option>
                    {difficulty.map((x: any, i: any) => (
                      <option key={i} value={i}>
                        {x}
                      </option>
                    ))}
                  </FlowbiteSelect>
                </div>
              </div>

              <div>
                <Label>Select Athlete</Label>
                <div className="mt-1">
                  <FlowbiteSelect
                    value={workout.trainer ?? ""}
                    onChange={handleChange}
                    name="trainer"
                  >
                    <option value="" disabled>
                      Choose
                    </option>
                    {props.trainers.map((x: any) => (
                      <option key={x.id} value={x.id}>
                        {x.title}
                      </option>
                    ))}
                  </FlowbiteSelect>
                </div>
              </div>

              <div>
                <Label>Select Intensity</Label>
                <div className="mt-1">
                  <FlowbiteSelect
                    value={workout.intensity ?? "Auto Assign"}
                    onChange={handleChange}
                    name="intensity"
                  >
                    <option value="" disabled>
                      Choose
                    </option>
                    {intensityArray.map((item, i) => (
                      <option key={i} value={item.id}>
                        {item.title}
                      </option>
                    ))}
                  </FlowbiteSelect>
                </div>
              </div>

              <div>
                <Label>Rounds</Label>
                <div className="mt-1">
                  <FlowbiteSelect
                    value={workout.rounds ?? ""}
                    name="rounds"
                    onChange={handleChange}
                  >
                    <option value="" disabled>
                      Choose
                    </option>
                    <option value="1">x1</option>
                    <option value="2">x2</option>
                    <option value="3">x3</option>
                    <option value="4">x4</option>
                    <option value="5">x5</option>
                    <option value="6">x6</option>
                    <option value="7">x7</option>
                  </FlowbiteSelect>
                </div>
              </div>

              <div>
                <Label>Cover Image</Label>
                <FileUploadZone
                  acceptedTypes={[
                    "image/jpg",
                    "image/png",
                    "image/jpeg",
                    "image/webp",
                  ]}
                  onImageChange={(image) => {
                    setImage(image.dataUri);
                    setImageName(image.name);
                    setImageType(image.type);
                  }}
                  defaultPreview={{
                    name: imageName,
                    url: image,
                    type: imageType,
                  }}
                  maxDimensions={{ width: 2000, height: 2000 }}
                  maxSize={10 * 1024 * 1024}
                />
              </div>
            </div>

            <div className="mt-12 border-t border-gray-200 pt-6 dark:border-gray-700">
              <h3 className="mb-6 text-sm font-semibold text-gray-900 dark:text-white">
                Visibility & Status
              </h3>
              <div className="flex flex-col space-y-4">
                <label className="relative inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    name="published"
                    checked={workout.published}
                    className="peer sr-only"
                    onChange={handleChange}
                  />
                  <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:size-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-hidden peer-focus:ring-4 peer-focus:ring-blue-300 dark:border-gray-600 dark:bg-gray-700 dark:peer-focus:ring-blue-800"></div>
                  <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">
                    Published
                  </span>
                </label>

                <label className="relative inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    name="featured"
                    checked={workout.featured}
                    className="peer sr-only"
                    onChange={handleChange}
                  />
                  <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:size-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-hidden peer-focus:ring-4 peer-focus:ring-blue-300 dark:border-gray-600 dark:bg-gray-700 dark:peer-focus:ring-blue-800"></div>
                  <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">
                    Featured
                  </span>
                </label>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              onClick={handleSubmit}
              disabled={isSubmitting || !formValid}
            >
              {isSubmitting ? "Creating..." : "Create"}
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </>
  );
};

export default AddWorkoutModal;
